import { format, parse } from 'date-fns';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  AutoBalanceTransfer,
  AutoBalanceTransferFrequency,
  CreateAutoBalanceTransferRequest,
  DatepickerSimple,
  FieldData,
  Select2,
  TextField,
  Tooltip,
  sanitizeToNumeric,
  useForm,
  BodyText,
} from 'vopay';
import { Toggle } from 'UI';
import InlineFieldRow from './InlineFieldRow';

export type TransferFormValues = {
  AutoBalanceTransferAmount: string;
  ScheduleStartDate: string;
  TypeOfFrequency: AutoBalanceTransferFrequency | '';
  SplitByCounterparty: boolean;
};

/** Currency is not a field: it comes from the route. */
export type TransferRequest = Omit<CreateAutoBalanceTransferRequest, 'Currency'>;

export const EMPTY_TRANSFER_FORM: TransferFormValues = {
  AutoBalanceTransferAmount: '0.01',
  ScheduleStartDate: '',
  TypeOfFrequency: '',
  SplitByCounterparty: false,
};

export function transferFormValues(transfer: AutoBalanceTransfer): TransferFormValues {
  const startDate = parse(transfer.ScheduleStartDate, 'yyyy-MM-dd', new Date());
  // A start date of today or earlier can't be resubmitted, so the user picks a new one.
  const canKeepStartDate = transfer.ScheduleStartDate > format(new Date(), 'yyyy-MM-dd');

  return {
    AutoBalanceTransferAmount: transfer.AutoBalanceTransferAmount,
    ScheduleStartDate: canKeepStartDate ? String(startDate.getTime()) : '',
    TypeOfFrequency: transfer.NameOfFrequency,
    SplitByCounterparty: transfer.SplitByCounterparty,
  };
}

type UseTransferFormConfigArgs = {
  initialValues: TransferFormValues;
  canSplitByCounterparty: boolean;
  onSubmit: (values: TransferRequest) => Promise<void>;
};

export default function useTransferFormConfig({
  initialValues,
  canSplitByCounterparty,
  onSubmit,
}: UseTransferFormConfigArgs) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.SetUpTransfer.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const today = format(new Date(), 'yyyy-MM-dd');

  const frequencyOptions: { value: AutoBalanceTransferFrequency; label: string }[] = [
    { value: 'daily', label: tGlobal('FrequencyOptions.daily') },
    { value: 'weekly', label: tGlobal('FrequencyOptions.weekly') },
    { value: 'biweekly', label: tGlobal('FrequencyOptions.biweekly') },
    { value: 'monthly', label: tGlobal('FrequencyOptions.monthly') },
  ];

  const scheduleFields: FieldData<TransferFormValues>[] = [
    {
      fieldName: 'AutoBalanceTransferAmount',
      label: () => t('MinimumBalance.label', { defaultValue: 'Minimum Balance to Trigger Transfer' }),
      tooltip: () => (
        <Tooltip
          type="hover"
          icon="info"
          position="right"
          hasArrow={false}
          information={
            <BodyText>
              {t('MinimumBalance.tooltip', {
                defaultValue:
                  'When your available balance reaches this amount, the full balance is transferred to your bank account — not just the part above it.',
              })}
            </BodyText>
          }
        />
      ),
      getInputState: () => 'required',
      showField: () => true,
      validate: () =>
        Yup.string()
          .required()
          .test(
            'minimum-balance',
            t('MinimumBalance.minValueError', {
              defaultValue: 'This value should be greater than or equal to $0.01.',
            }),
            (value) => Number(sanitizeToNumeric(value)) >= 0.01,
          ),
      // The backend accepts digits and a dot only, so the grouping commas must go.
      serializeField: (values) => Number(sanitizeToNumeric(values.AutoBalanceTransferAmount)),
      renderField: ({ getFormikData }) => (
        <TextField
          preset="currency"
          variant="outlineShadow"
          placeholder="100.00"
          {...getFormikData<TransferFormValues['AutoBalanceTransferAmount']>({})}
        />
      ),
    },
    {
      fieldName: 'ScheduleStartDate',
      label: () => t('StartDate.label', { defaultValue: 'Start Date' }),
      tooltip: () => (
        <Tooltip
          type="hover"
          icon="info"
          position="right"
          hasArrow={false}
          information={
            <BodyText>{t('StartDate.tooltip', { defaultValue: 'The first day the schedule can run.' })}</BodyText>
          }
        />
      ),
      getInputState: () => 'required',
      showField: () => true,
      validate: () =>
        Yup.string()
          .required()
          .test(
            'start-date',
            t('StartDate.invalidError', {
              defaultValue: 'Invalid date. Please choose a date after {{startDate}}',
              startDate: today,
            }),
            // The backend rejects today as well as past dates.
            (value) => !!value && format(new Date(Number(value)), 'yyyy-MM-dd') > today,
          ),
      serializeField: (values) => format(new Date(Number(values.ScheduleStartDate)), 'yyyy-MM-dd'),
      renderField: ({ getFormikData }) => (
        <DatepickerSimple variant="outlineShadow" {...getFormikData<TransferFormValues['ScheduleStartDate']>({})} />
      ),
    },
    {
      fieldName: 'TypeOfFrequency',
      label: () => t('Frequency.label', { defaultValue: 'Frequency' }),
      tooltip: () => (
        <Tooltip
          type="hover"
          icon="info"
          position="right"
          hasArrow={false}
          information={
            <BodyText>
              {t('Frequency.tooltip', { defaultValue: 'How often the transfer runs once it has started.' })}
            </BodyText>
          }
        />
      ),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => (
        <Select2
          {...getFormikData<TransferFormValues['TypeOfFrequency']>({})}
          options={frequencyOptions}
          placeholder={t('Frequency.placeholder', { defaultValue: 'Select a frequency' })}
        />
      ),
    },
  ];

  const settingFields: FieldData<TransferFormValues>[] = [
    {
      fieldName: 'SplitByCounterparty',
      label: () => t('SplitByCounterparty.label', { defaultValue: 'Split by counterparty' }),
      tooltip: () => (
        <Tooltip
          type="hover"
          icon="info"
          position="right"
          hasArrow={false}
          width="340px"
          information={
            <BodyText>
              {t('SplitByCounterparty.tooltip', {
                defaultValue:
                  'Instead of one transfer for your full available balance, we’ll send a separate transfer for each counterparty whose payments make up that balance. Each transfer is labelled with the counterparty’s name so you can match it on your bank statement. Amounts that can’t be linked to a counterparty, like fees or adjustments, are sent together as one “Unattributed” transfer. The total amount transferred stays the same.',
              })}
            </BodyText>
          }
        />
      ),
      getInputState: () => 'optional',
      showField: () => canSplitByCounterparty,
      renderField: ({ getFormikData }) => {
        const field = getFormikData<TransferFormValues['SplitByCounterparty']>({});

        return <Toggle name={field.name} checked={field.value} onChange={field.onChange} />;
      },
    },
  ];

  // A hidden SplitByCounterparty serializes to undefined, so it is left out of the request rather than sent as false.
  const { ctx, renderFormElements } = useForm<TransferFormValues, TransferRequest>({
    config: [...scheduleFields, ...settingFields],
    initialValues,
    // Update mode's values arrive after the first render.
    enableReinitialize: true,
    onSubmit,
  });

  return {
    ctx,
    renderFields: () => renderFormElements({ config: scheduleFields }),
    renderSettings: () => renderFormElements({ config: settingFields, override: InlineFieldRow }),
  };
}
